/**
 * O corpo do Relatório, igual no preview e na impressão.
 *
 * Ordem: Resumo executivo → (Comparativo) → Análise técnica de cada assunto
 * (memoriais, premissas, Caracterização do recurso, infraestrutura) →
 * (Fernando de Noronha) → (Camadas de contexto) → Nota metodológica.
 */

import logoSvg from "../Navbar/assets/logo.svg";
import type { ReportData, ReportSubject } from "../ReportComponent/model/types";
import { ReportFooter, ReportLegends } from "./components";
import { ComparisonSection } from "./sections/ComparisonSection";
import { ExecutiveSummary } from "./sections/ExecutiveSummary";
import { InfrastructureSection } from "./sections/InfrastructureSection";
import { ENERGY_TITLE, MemorialSection } from "./sections/MemorialSection";
import { ResourceSection, ResourceTable } from "./sections/ResourceSection";
import { INK, Lead, SectionTitle, SubTitle } from "./sections/primitives";

const SCOPE_LABEL: Record<ReportData["scope"], string> = {
  marcacao: "Análise de área",
  comparativo: "Comparativo entre duas áreas",
  estado: "Pernambuco",
};

function Header({ data }: { data: ReportData }) {
  const date = new Date(data.metadata.timestamp).toLocaleDateString("pt-BR", {
    day: "2-digit",
    month: "2-digit",
    year: "numeric",
  });
  return (
    <header className="report-header mb-4">
      <div className="flex justify-between items-start">
        <div>
          <h1 className="text-[1.7rem] font-bold leading-tight" style={{ color: INK.brand }}>
            Relatório Técnico de Potencial Energético
          </h1>
          <span className="text-[0.85rem]" style={{ color: INK.secondary }}>
            {SCOPE_LABEL[data.scope]} ·{" "}
            {data.energies.map((e) => ENERGY_TITLE[e]).join(" e ")} · nível{" "}
            {data.levelLabel} · {date}
          </span>
        </div>
        <img src={logoSvg} alt="Plataforma Energética de Pernambuco" className="h-12 w-auto" />
      </div>
      {(data.metadata.projectName || data.metadata.projectDescription) && (
        <div className="mt-4">
          {data.metadata.projectName && (
            <h2 className="text-[1.05rem] font-bold mb-1" style={{ color: INK.brand }}>
              {data.metadata.projectName}
            </h2>
          )}
          {data.metadata.projectDescription && (
            <p className="text-[0.85rem] leading-relaxed" style={{ color: INK.primary }}>
              {data.metadata.projectDescription}
            </p>
          )}
        </div>
      )}
      <hr className="border-0 border-t mt-4 mb-2" style={{ borderColor: INK.rule }} />
    </header>
  );
}

function SubjectAnalysis({
  data,
  subject,
  number,
}: {
  data: ReportData;
  subject: ReportSubject;
  number: string;
}) {
  return (
    <section>
      <SectionTitle number={number}>Análise técnica — {subject.name}</SectionTitle>
      {data.energies.map((energy) => {
        const analysis = subject.analyses[energy];
        return (
          <div key={energy}>
            <h3
              className="text-[1.02rem] font-bold mt-5 mb-1 pb-1 border-b"
              style={{ color: INK.brand, borderColor: INK.rule }}
            >
              {ENERGY_TITLE[energy]}
            </h3>
            {analysis ? (
              <>
                <MemorialSection analysis={analysis} levelLabel={data.levelLabel} />
                <ResourceSection
                  characterization={analysis.characterization}
                  stateCharacterization={
                    data.stateReference?.analyses[energy]?.characterization ?? null
                  }
                />
              </>
            ) : null}
          </div>
        );
      })}
      {subject.kind !== "estado" && (
        <InfrastructureSection subject={subject} systemMode={data.systemMode} />
      )}
    </section>
  );
}

function NoronhaSection({ data, number }: { data: ReportData; number: string }) {
  if (!data.noronha) return null;
  return (
    <section>
      <SectionTitle number={number}>Fernando de Noronha</SectionTitle>
      <Lead>
        O arquipélago tem regime de sol e vento próprio e não entra nas projeções de
        potencial do estado. Este quadro traz apenas a caracterização do recurso na
        ilha, com os dados aeroespaciais disponíveis para ela.
      </Lead>
      {data.energies.map((energy) => {
        const c = data.noronha?.[energy];
        const available = c?.variables.filter((v) => v.available) ?? [];
        return (
          <div key={energy}>
            <SubTitle>{ENERGY_TITLE[energy]}</SubTitle>
            {available.length === 0 ? (
              <p className="text-[0.8rem]" style={{ color: INK.secondary }}>
                Sem dados aeroespaciais de {ENERGY_TITLE[energy].toLowerCase()} para a ilha.
              </p>
            ) : (
              <ResourceTable variables={available} />
            )}
          </div>
        );
      })}
    </section>
  );
}

function ContextSection({ data, number }: { data: ReportData; number: string }) {
  if (!data.context) return null;
  return (
    <section>
      <SectionTitle number={number}>Camadas de contexto</SectionTitle>
      <Lead>
        Camadas ligadas no painel no momento da geração do relatório.
        {data.scope === "marcacao"
          ? " As contagens consideram apenas a área analisada."
          : data.scope === "comparativo"
            ? " O mapa e as contagens abrangem todo o estado."
            : ""}
      </Lead>
      {data.context.mapImage && (
        <img
          src={data.context.mapImage}
          alt="Mapa com as camadas de contexto"
          className="w-full h-auto max-h-[380px] object-contain border rounded my-2"
          style={{ borderColor: INK.rule }}
        />
      )}
      {data.context.legends.length > 0 ? (
        <ReportLegends
          legends={data.context.legends}
          isAreaReport={data.scope === "marcacao"}
        />
      ) : (
        <p className="text-[0.8rem]" style={{ color: INK.secondary }}>
          Nenhuma camada de contexto estava ligada.
        </p>
      )}
    </section>
  );
}

function MethodologyNote({ number }: { number: string }) {
  return (
    <section>
      <SectionTitle number={number}>Nota metodológica</SectionTitle>
      <ul
        className="list-disc pl-5 text-[0.78rem] leading-relaxed flex flex-col gap-1"
        style={{ color: INK.primary }}
      >
        <li>
          Os números de potencial são estimativas de planejamento, não substituem estudo
          de viabilidade, medição local nem a Consulta de Viabilidade à distribuidora.
        </li>
        <li>
          A área propícia vem dos rasters de projeção do Atlas; o Memorial de Cálculo da
          Área Propícia atribui cada porção excluída a uma única restrição, por ordem de
          incidência.
        </li>
        <li>
          As premissas do Memorial de Cálculo da Geração estão listadas com a fonte de
          cada uma; as marcadas como "em validação" ainda passam por revisão da equipe
          técnica.
        </li>
        <li>
          A Caracterização do recurso usa os dados aeroespaciais (grade de 200 m) e é
          descritiva — não altera o cálculo de geração.
        </li>
        <li>
          No relatório de Pernambuco, o memorial da área propícia é calculado numa grade
          reduzida (~120 m), suficiente para percentuais estaduais.
        </li>
      </ul>
    </section>
  );
}

export function ReportDocument({ data }: { data: ReportData }) {
  let section = 1;
  const next = () => String(section++);

  return (
    <div className="report-document">
      <Header data={data} />
      <SectionTitle number={next()} pageBreak={false}>
        Resumo executivo
      </SectionTitle>
      {data.subjects.map((subject) => (
        <ExecutiveSummary
          key={subject.id}
          subject={subject}
          energies={data.energies}
          levelLabel={data.levelLabel}
        />
      ))}

      {data.scope === "comparativo" && <ComparisonSection data={data} number={next()} />}

      {data.subjects.map((subject) => (
        <SubjectAnalysis key={subject.id} data={data} subject={subject} number={next()} />
      ))}

      {data.noronha && <NoronhaSection data={data} number={next()} />}
      {data.context && <ContextSection data={data} number={next()} />}
      <MethodologyNote number={next()} />
      <ReportFooter />
    </div>
  );
}
